import { compactFlatten } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';

import { useManageScanPhotosSync, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getUniqueScanPhotos, isGroupScoped, removeDuplicateScanPhotos, ScanPhoto } from '@kaiizen/kai-scan-template-system';

import { getCategorizedGroups, getFilteredGroups } from '@/Client/Lib/scanLib';
import { useConfirmationModal, useToast } from '@/Client/State';
import { LAYOUT2 } from '@/Client/Styles';
import { Button } from '@/Client/Widgets';
import { groupNamePresenter, taskNamePresenter } from '@kaiizen/kai-presenters';
import { ScanEditorPageTitleBar } from '../../Widgets/ScanEditorPageTitleBar';
import { PhotoGallerySet } from './PhotoGallerySet';

const SCAN_STYLE = css`
  label: SCAN_STYLE;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const CONTENT_STYLE = css`
  label: CONTENT_STYLE;
  flex: 1;
  overflow-y: auto;
`;

const BUTTON_CONTAINER_STYLE = css`
  label: BUTTON_CONTAINER_STYLE;
  display: flex;
  justify-content: flex-end;
  padding: ${LAYOUT2.spacing2}px;
`;

/**
 * The pre-newScopeBuilderFlow photo gallery. Users who have not opted into the
 * new experience (KAIDEV-3790) must keep this UI; the new FocusedPhotoGallery
 * is reachable only through AdminScanPageNew, which App.tsx gates on the
 * newScopeBuilderFlow feature flag.
 */
export const AllScanPhotosLegacy = ({
  filterRegex,
}: {
  filterRegex?: RegExp;
}) => {
  const { scanRoot, scan, scanDataMutators, scans } = useScanEditingContextSlice(['scanRoot', 'scan', 'scanDataMutators', 'scans']);
  const filteredGroups = getFilteredGroups(scanRoot?.groups, filterRegex);
  const groupsWithScopeOrPhotos = filteredGroups?.filter(group => group.optimized.allPhotos.length > 0 || isGroupScoped(group)) || [];
  const categorizedGroups = getCategorizedGroups(groupsWithScopeOrPhotos);
  const { resyncScanPhotos } = useManageScanPhotosSync();
  const confirm = useConfirmationModal();
  const { showToast, showErrorToast } = useToast();

  const handleResyncClick = async () => {
    if (!scan) return;

    await confirm({
      title: 'Resync Scan Photos',
      message: `This will check which photos are synced and queue any missing for resync. Continue?`,
      confirmLabel: 'Resync',
      onConfirm: async () => {
        try {
          const result = await resyncScanPhotos(scans?.map(s => s.id) ?? [scan.id]);
          const { numSynced, numResyncing, errors } = result;

          const parts = [];
          if (numSynced > 0) parts.push(`${numSynced} already synced`);
          if (numResyncing > 0) parts.push(`${numResyncing} queued for resync`);
          const message = parts.length ? parts.join('. ') : 'No photos to resync.';
          showToast(message);
          if (errors.length > 0) {
            showErrorToast(errors[0], errors.length === 1 ? 'queue photo for resync' : `queue ${errors.length} photos for resync`);
          }
        } catch (error) {
          showErrorToast(error, 'resync scan photos');
        }
      },
    });
  };

  if (!scan || !scanRoot) return null;
  return (
    <div css={SCAN_STYLE}>
      <ScanEditorPageTitleBar
        scanLifecycleStatus={scan.scanLifecycleStatus}
        title={`All Photos (${scanRoot.optimized.allPhotos.length})`}
        showCost={false}
      />
      <div css={CONTENT_STYLE}>
        <PhotoGallerySet
          scanId={scan.id}
          headerIsSticky
          galleries={compactFlatten([
            {
              header: 'Overview',
              scanPart: scanRoot,
              sections: [
                {
                  header: 'Curbside Photos',
                  scanPart: scanRoot,
                  scanPartContext: {},
                  photos: scanRoot.photos ?? [],
                  onAddPhotos: (photos: ScanPhoto[]) =>
                    scanDataMutators.updateScan({
                      photos: getUniqueScanPhotos(scanRoot.photos, photos),
                    }),
                },
              ],
            },
            Object.keys(categorizedGroups).sort().flatMap(category => (
              categorizedGroups[category].length > 0
                ? [{
                  header: `${category} groups`,
                  sections: categorizedGroups[category].flatMap(
                    group => {
                      const groupSection = {
                        header: groupNamePresenter(group),
                        scanPart: group,
                        scanPartContext: { groupId: group.id },
                        photos: group.optimized.allPhotos,
                        onAddPhotos: (photos: ScanPhoto[]) =>
                          scanDataMutators.updateGroup(group.id, {
                            photos: getUniqueScanPhotos(group.photos, photos),
                          }),
                        onRemoveDuplicates: () => {
                          if (!group.photos) return;
                          scanDataMutators.updateGroup(group.id, {
                            photos: removeDuplicateScanPhotos(group.photos),
                          });
                        },
                      };

                      const taskSections = (group.tasks ?? [])
                        .filter(task => (task.photos ?? []).length > 0)
                        .map(task => ({
                          header: taskNamePresenter(task),
                          scanPart: task,
                          scanPartContext: { groupId: group.id, taskId: task.id },
                          photos: task.photos ?? [],
                          onAddPhotos: (photos: ScanPhoto[]) =>
                            scanDataMutators.updateTask(group.id, task.id, {
                              photos: getUniqueScanPhotos(task.photos, photos),
                            }),
                          onRemoveDuplicates: () => {
                            if (!task.photos) return;
                            scanDataMutators.updateTask(group.id, task.id, {
                              photos: removeDuplicateScanPhotos(task.photos),
                            });
                          },
                        }));

                      return [groupSection, ...taskSections];
                    },
                  ),
                }]
                : []
            )),
          ])}
        />

        <div css={BUTTON_CONTAINER_STYLE}>
          <Button onClick={handleResyncClick}>Resync Scan Photos</Button>
        </div>
      </div>
    </div>
  );
};
